import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Navigate } from "react-router-dom"
import { logout } from "../store/slices/authSlice"
import { fetchClientsByUserId } from "../store/slices/clientsSlice"
import { fetchOrganizations } from "../store/slices/settingOrganizationSlice"
import {
  markPropertySignupRequired,
  userNeedsPropertySetup,
} from "../lib/utils/propertySignupRedirect"

/**
 * Blocks main app (calendar, dashboard, etc.) until the user has at least one property/client.
 * Redirects authenticated users without properties to /signup step 2 (property details).
 */
const PropertyRequiredGuard = ({ children }) => {
  const dispatch = useDispatch()
  const token = useSelector((state) => state.auth?.token)
  const user = useSelector((state) => state.user?.profile)
  const userStatus = useSelector((state) => state.user?.status)
  const clients = useSelector((state) => state.clients?.clients ?? [])
  const clientsStatus = useSelector((state) => state.clients?.status ?? "idle")
  const clientsError = useSelector((state) => state.clients?.error)
  const organizations = useSelector((state) => state.organizations?.organizations ?? [])
  const clientsFetchStartedRef = useRef(false)
  const [clientsRechecked, setClientsRechecked] = useState(false)

  useEffect(() => {
    if (!token) return
    if (!user && userStatus === "failed") {
      dispatch(logout())
    }
  }, [token, user, userStatus, dispatch])

  useEffect(() => {
    if (!token || !user?.id) return
    dispatch(fetchOrganizations({ userId: user.id }))
  }, [token, user?.id, dispatch])

  useEffect(() => {
    if (!token || !user?.id) return
    if (clientsStatus !== "idle") return
    if (clientsFetchStartedRef.current) return
    clientsFetchStartedRef.current = true
    dispatch(
      fetchClientsByUserId({
        userId: user.id,
        organizationId: organizations?.[0]?.id,
      }),
    )
  }, [token, user?.id, clientsStatus, dispatch, organizations])

  useEffect(() => {
    if (!token || !user?.id || !organizations?.[0]?.id) return
    if (clients.length > 0) return
    if (clientsStatus !== "failed") return
    dispatch(
      fetchClientsByUserId({
        userId: user.id,
        organizationId: organizations[0].id,
        force: true,
      }),
    )
  }, [token, user?.id, organizations, clients.length, clientsStatus, dispatch])

  // Stale empty list (fetched before the property was created) must be refreshed once.
  useEffect(() => {
    if (!token || !user?.id) return
    if (clients.length > 0) {
      setClientsRechecked(true)
      return
    }
    if (clientsStatus === "idle" || clientsStatus === "loading") return
    if (clientsRechecked) return
    dispatch(
      fetchClientsByUserId({
        userId: user.id,
        organizationId: organizations?.[0]?.id,
        force: true,
      }),
    ).finally(() => setClientsRechecked(true))
  }, [token, user?.id, clients.length, clientsStatus, clientsRechecked, organizations, dispatch])

  if (!token) {
    return children
  }

  if (!user && userStatus === "failed") {
    return <Navigate to="/login" replace />
  }

  if (
    clientsRechecked &&
    userNeedsPropertySetup(clients, clientsStatus, clientsError)
  ) {
    markPropertySignupRequired()
    return <Navigate to="/signup" replace />
  }

  if (user?.id && clientsStatus === "failed" && clients.length === 0 && clientsRechecked) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: "12px",
          fontSize: "16px",
          color: "#666",
        }}
      >
        <div>Unable to load your properties. Please try again.</div>
        <button
          type="button"
          onClick={() =>
            dispatch(
              fetchClientsByUserId({
                userId: user.id,
                organizationId: organizations?.[0]?.id,
                force: true,
              }),
            )
          }
        >
          Try again
        </button>
      </div>
    )
  }

  return children
}

export default PropertyRequiredGuard
